import config from '../../config';
import API from '../api';

const { baseUrl } = config.coreApi;

//#region Types
/**
 * @typedef SiteType
 * @type {Object}
 * @property {string} displayName - Display name of the site type.
 * @property {string} id - ID of the site type.
 * @property {number} radius - Radius of the site type.
 */

/**
 * @typedef Site
 * @type {Object}
 * @property {string} address - Address of the site.
 * @property {string[]} attachedDevices - Array of attached device identifiers.
 * @property {string} countryCode - Country code of the site.
 * @property {string} createTime - Creation time of the site.
 * @property {("DATASOURCE_UNSPECIFIED" | "TRAFFIC_M" | "AUDIENCE_M" | "WIFI_M" | "MOBILE_M")[]} datasources - Available data sources for the site.
 * @property {string} deleteTime - Deletion time of the site.
 * @property {string} description - Description of the site.
 * @property {string} displayName - Display name of the site.
 * @property {string} id - ID of the site.
 * @property {{type: ("Point" | "Polygon"), coordinates: (number[] | number[][][])}} [geometry] - GeoJSON site geometry.
 * @property {number} latitude - Latitude coordinate of the site.
 * @property {number} longitude - Longitude coordinate of the site.
 * @property {string} name - Name of the site.
 * @property {string} projectId - Project ID associated with the site.
 * @property {("SITE_SOLUTION_UNSPECIFIED" | "TRAFFIC" | "AUDIENCE" | "CUSTOM")} solution - Solution type of the site.
 * @property {("SITE_STATUS_UNSPECIFIED" | "ENABLED" | "DISABLED" | "NOT_CONFIGURED")} status - Status of the site.
 * @property {SiteType} type - Type details of the site.
 * @property {string} updateTime - Update time of the site.
 */
//#endregion

//#region CREATE
/**
 * Creates a new site.
 * @param {Site} site - Site to be created.
 * @returns {Site} Created site.
 */
const createSite = async (site, accessToken = null) => {
  await API.refreshTokenWhenNeeded();
  const url = `${baseUrl}/projects/${site.projectId}/sites`;
  return fetch(url, {
    method: 'POST',
    headers: accessToken
      ? { ...API.headers, Authorization: `Bearer ${accessToken}`, 'X-Request-Id': crypto.randomUUID() }
      : { ...API.headers, 'X-Request-Id': crypto.randomUUID() },
    body: JSON.stringify(site)
  }).then(res => res.json());
};
//#endregion

//#region READ
/**
 * Lists existing sites.
 * @param {string} pid - Site group ID.
 * @param {string} page - page token (first page is defined as empty string).
 * @param {string} filter - Filter expression.
 * @param {string} orderBy - Order expression.
 * @returns {{sites: Site[], nextPageToken: string, totalSize: number}} List of sites.
 */
const getSites = async (pid, page = '', filter = '', orderBy = 'displayName, id', pageSize = 40) => {
  await API.refreshTokenWhenNeeded();

  const url = `${baseUrl}/projects/${pid}/sites?pageSize=${pageSize}&pageToken=${page}&filter=${filter}&orderBy=${orderBy}`;
  return fetch(url, {
    headers: { ...API.headers, 'X-Request-Id': crypto.randomUUID() },
    method: 'GET'
  }).then(res => API.generateResponse(res));
};

/**
 * Lists Sites with their Assets and attached Devices.
 * @param {string} pid - Project ID.
 * @param {string} page - Page token; omit for the first page.
 * @param {number} pageSize - Maximum number of Sites to return.
 * @returns {{sites: Site[], nextPageToken: string, totalSize: number}} Sites with Assets page.
 */
const getSitesWithAssets = async (pid, page = '', pageSize = 200) => {
  await API.refreshTokenWhenNeeded();
  const url = `${baseUrl}/projects/${pid}/sites:withAssets?pageSize=${pageSize}&pageToken=${page}`;

  return fetch(url, {
    headers: { ...API.headers, 'X-Request-Id': crypto.randomUUID() },
    method: 'GET'
  }).then(res => API.generateResponse(res));
};

/**
 * Lists every Site with Assets in a Project, following page tokens until all pages are loaded.
 * @param {string} pid - Project ID.
 * @param {number} pageSize - Maximum number of Sites to request per page.
 * @returns {{sites: Site[], nextPageToken: string, totalSize: number}} Complete Sites with Assets list.
 */
const getAllSitesWithAssets = async (pid, pageSize = 200) => {
  const sites = [];
  const visitedPageTokens = new Set();
  let pageToken = '';
  let totalSize = 0;

  do {
    if (visitedPageTokens.has(pageToken)) {
      throw new Error('The Sites with Assets API returned a repeated page token.');
    }
    visitedPageTokens.add(pageToken);

    const response = await getSitesWithAssets(pid, pageToken, pageSize);
    sites.push(...(response.sites || []));
    totalSize = response.totalSize ?? totalSize;
    pageToken = response.nextPageToken || '';
  } while (pageToken);

  return { sites, nextPageToken: '', totalSize: totalSize || sites.length };
};

/**
 * Lists existing sites by company.
 * @param {string} cid - Company ID.
 * @param {string} page - page token (first page is defined as empty string).
 * @param {string} filter - Filter expression.
 * @param {string} orderBy - Order expression.
 * @returns {{sites: Site[], nextPageToken: string, totalSize: number}} List of sites.
 */
const getSitesByCompany = async (cid, page = '', filter = '', orderBy = 'displayName, id', pageSize = 40) => {
  await API.refreshTokenWhenNeeded();

  const url = `${baseUrl}/companies/${cid}/sites?pageSize=${pageSize}&pageToken=${page}&filter=${filter}&orderBy=${orderBy}`;
  return fetch(url, {
    headers: { ...API.headers, 'X-Request-Id': crypto.randomUUID() },
    method: 'GET'
  }).then(res => API.generateResponse(res));
};

/**
 * Gets a single site.
 * @param {string} pid - Site group ID.
 * @param {string} sid - Site ID.
 * @returns {Site} Site.
 */
const getSite = async (pid, sid) => {
  await API.refreshTokenWhenNeeded();

  const url = `${baseUrl}/projects/${pid}/sites/${sid}`;
  return fetch(url, {
    headers: { ...API.headers, 'X-Request-Id': crypto.randomUUID() },
    method: 'GET'
  }).then(res => API.generateResponse(res));
};

/**
 * Lists existing site types.
 * @param {string} page - page token (first page is defined as empty string).
 * @param {string} filter - Filter expression.
 * @param {string} orderBy - Order expression.
 * @returns {{siteTypes: SiteType[], nextPageToken: string}} List of site types.
 */
const getSiteTypes = async (page = '', filter = '', orderBy = 'displayName, id') => {
  await API.refreshTokenWhenNeeded();

  const url = `${baseUrl}/sitetypes?pageSize=40&pageToken=${page}&filter=${filter}&orderBy=${orderBy}`;
  return fetch(url, {
    headers: { ...API.headers, 'X-Request-Id': crypto.randomUUID() },
    method: 'GET'
  }).then(res => API.generateResponse(res));
};

//#endregion

//#region UPDATE
/**
 * Transfers sites to another site group.
 * @param {string} pid - Site group ID.
 * @param {Object} trasferObject - Object containing site names and target project ID.
 * @returns {{}} Response from the API.
 */
const transferSites = async (pid, trasferObject) => {
  await API.refreshTokenWhenNeeded();
  const url = `${baseUrl}/projects/${pid}/sites:move`;
  return fetch(url, {
    method: 'PATCH',
    headers: { ...API.headers, 'X-Request-Id': crypto.randomUUID() },
    body: JSON.stringify(trasferObject)
  }).then(res => API.generateResponse(res));
};

/**
 * Updates a site.
 * @param {Site} site - Site to be updated.
 * @param {string[]} fields - Site fields being updated. Comma separated.
 * @returns {Site} Site updated.
 */
const updateSite = async (site, fields) => {
  await API.refreshTokenWhenNeeded();
  const paths = `updateMask=${fields.map(field => (field === 'type' ? 'type.id' : field)).join(',')}`;
  const body = {};
  fields.forEach(field => {
    body[field] = site[field];
  });
  const url = `${baseUrl}/projects/${site.projectId}/sites/${site.id}?${paths}`;

  return fetch(url, {
    method: 'PATCH',
    headers: { ...API.headers, 'X-Request-Id': crypto.randomUUID() },
    body: JSON.stringify(body)
  }).then(res => API.generateResponse(res));
};

/**
 * Sets the list of attached devices for a site.
 * @param {Site} site - Site to be updated.
 * @returns {{attachedDevicesObject: {deviceNames: string[], name: string}}} Attached devices object.
 */
const updateAttachedDevices = async site => {
  await API.refreshTokenWhenNeeded();
  const url = `${baseUrl}/projects/${site.projectId}/sites/${site.id}:setAttachedDevices`;
  return fetch(url, {
    method: 'PATCH',
    headers: { ...API.headers, 'X-Request-Id': crypto.randomUUID() },
    body: JSON.stringify({ deviceNames: site.attachedDevices.map(id => `devices/${id}`) })
  }).then(res => res.json());
};
//#endregion

//#region DELETE
/**
 * Deletes a single site.
 * @param {string} cid - Company ID.
 * @param {string} pid - Site group ID.
 * @param {string} sid - Site ID.
 * @returns {{}}
 */
const deleteSite = async (pid, sid) => {
  await API.refreshTokenWhenNeeded();

  const url = `${baseUrl}/projects/${pid}/sites/${sid}`;
  return fetch(url, {
    method: 'DELETE',
    headers: { ...API.headers, 'X-Request-Id': crypto.randomUUID() }
  }).then(res => res.json());
};
//#endregion

const createUploadUrl = async pid => {
  await API.refreshTokenWhenNeeded();
  const url = `${baseUrl}/projects/${pid}/sites:createUploadUrl`;

  return fetch(url, {
    method: 'POST',
    headers: { ...API.headers, 'X-Request-Id': crypto.randomUUID() },
    body: JSON.stringify({ parent: `projects/${pid}` })
  }).then(res => res.json());
};

const uploadSiteFile = async (uploadUrl, file) => {
  await API.refreshTokenWhenNeeded();
  return fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      ...API.headers,
      'X-Request-Id': crypto.randomUUID(),
      'Content-Type': 'application/octet-stream'
    },
    body: file
  }).then(res => res.status);
};

export {
  createSite,
  getSites,
  getSitesWithAssets,
  getAllSitesWithAssets,
  getSitesByCompany,
  getSite,
  getSiteTypes,
  transferSites,
  updateSite,
  updateAttachedDevices,
  deleteSite,
  createUploadUrl,
  uploadSiteFile
};
